import { z } from 'zod'

/**
 * Reports & Analytics contracts. These Zod schemas are the single source of
 * truth for the "Performa Bisnis" feature — both the (currently mocked) API
 * layer and the UI derive their types from here via `z.infer`.
 *
 * When the backend metrics endpoint ships, keep these schemas aligned with the
 * real payload and `api.ts` is the only file that changes.
 */

/** Chart grouping granularity, driven by the "Periode performa" tabs. */
export const performancePeriodSchema = z.enum(['daily', 'weekly', 'monthly'])

/** The four headline metrics shown as selectable cards above the chart. */
export const performanceMetricIdSchema = z.enum(['revenue', 'orders', 'aov', 'buyers'])

/** A single bar in the chart: an x-axis label and its numeric value. */
export const performancePointSchema = z.object({
  label: z.string(),
  value: z.number(),
})

export const performanceMetricSchema = z.object({
  id: performanceMetricIdSchema,
  label: z.string(),
  /** Formatted display value for the metric card, e.g. "Rp12.480.000". */
  display: z.string(),
  /** Period-over-period change, e.g. "+9,7%". */
  delta: z.string(),
  deltaPositive: z.boolean(),
  /** Chart title shown above the chart when this metric is active. */
  chartTitle: z.string(),
  /** Whether chart values are currency (formats axis/tooltip as Rupiah). */
  currency: z.boolean(),
  series: z.array(performancePointSchema),
})

export const performanceDatasetSchema = z.object({
  /** Human caption fallback, e.g. "7 hari terakhir · 23–29 Okt 2026". */
  rangeLabel: z.string(),
  metrics: z.array(performanceMetricSchema),
})

export type PerformancePeriod = z.infer<typeof performancePeriodSchema>
export type PerformanceMetricId = z.infer<typeof performanceMetricIdSchema>
export type PerformancePoint = z.infer<typeof performancePointSchema>
export type PerformanceMetric = z.infer<typeof performanceMetricSchema>
export type PerformanceDataset = z.infer<typeof performanceDatasetSchema>

/** Request params the chart passes to the API (and that the real BE will accept). */
export interface PerformanceQuery {
  period: PerformancePeriod
  /** ISO date (yyyy-mm-dd) of the selected range start. */
  startDate: string
  /** ISO date (yyyy-mm-dd) of the selected range end. */
  endDate: string
}
